/* eslint-disable react-refresh/only-export-components */
import { createContext, isValidElement, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { translateUiText, interpolateMessage } from '../../../ui/locale/centralLocalization';
import { useOptionalLanguage } from '../../../context/LanguageContext';
import './sales-fonts.css';
import './sales-locale.css';
import { publishDepartmentLocale } from '../../../ui/locale/departmentLocale';

export type SalesLanguage = 'en' | 'ar';
const STORAGE_KEY = 'sales.language';
import { getAccountLanguage } from '../../../features/account-settings/preferenceEvents';
import { useStaffLanguageSync } from '../../../features/account-settings/useStaffLanguageSync';
const listeners = new Set<() => void>();
let activeLanguage: SalesLanguage | null = null;
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const getSnapshot = () => activeLanguage;
const getServerSnapshot = () => null;
/** Display helpers outside components use the active Sales language. */
export const getSalesLanguage = (): SalesLanguage => activeLanguage ?? 'en';
function publishLanguage(language: SalesLanguage | null) {
  if (language === activeLanguage) return;
  activeLanguage = language;
  listeners.forEach(listener => listener());
}

/** For sales-facing shared overlays rendered above the sales provider. */
export function useActiveSalesLanguage(): SalesLanguage | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Shared engine adapter; preserve the department's authored-copy namespace. */
export function translateSalesText(text: string, language: SalesLanguage): string {
  return translateUiText(text, language, 'sales');
}

/** Render-time helper for UI copy; non-string values retain their identity. */
export function salesText<T>(value: T): T {
  return (typeof value === 'string' ? translateSalesText(value, activeLanguage ?? 'en') : value) as T;
}
const metadataKeys = new Set(['label', 'title', 'header', 'hint', 'caption', 'emptyText', 'message', 'okText', 'cancelText']);
const dataKeys = new Set(['name', 'value', 'key', 'id', 'course', 'data', 'student', 'customer', 'row', 'lead', 'employee', 'notes', 'content', 'description']);
const configCaches: Record<SalesLanguage, WeakMap<object, unknown>> = { en: new WeakMap(), ar: new WeakMap() };
function translateLabels<T>(config: T, language: SalesLanguage): T {
  if (!config || typeof config !== 'object' || isValidElement(config)) return config;
  const prototype = Object.getPrototypeOf(config);
  if (!Array.isArray(config) && prototype !== Object.prototype && prototype !== null) return config;
  const cache = configCaches[language];
  if (cache.has(config)) return cache.get(config) as T;
  let result: object = config;
  const preserveDisplayLabel = (config as { translate?: boolean }).translate === false;
  for (const [key, value] of Object.entries(config)) {
    if (dataKeys.has(key) || (preserveDisplayLabel && (key === 'label' || key === 'title'))) continue;
    const next = metadataKeys.has(key) && typeof value === 'string' ? translateSalesText(value, language) : translateLabels(value, language);
    if (next !== value) {
      if (result === config) result = Array.isArray(config) ? [...config] : Object.assign(Object.create(prototype), config);
      (result as Record<string, unknown>)[key] = next;
    }
  }
  cache.set(config, result);
  return result as T;
}
export function salesLabels<T>(config: T): T { return translateLabels(config, activeLanguage ?? 'en'); }
export function salesFormat(template: string, values: readonly unknown[]): string {
  return formatSalesText(template, values, activeLanguage ?? 'en');
}
function formatSalesText(template: string, values: readonly unknown[], language: SalesLanguage): string {
  return interpolateTemplate(translateSalesText(template, language), values);
}
function interpolateTemplate(template: string, values: readonly unknown[]): string {
  return interpolateMessage(template, values);
}

interface SalesLocale {
  language: SalesLanguage;
  setLanguage: (language: SalesLanguage) => void;
  toggleLanguage: () => void;
  t: <T>(value: T) => T;
  format: typeof salesFormat;
  labels: typeof salesLabels;
}
const englishFallback: SalesLocale = { language: 'en', setLanguage: () => undefined, toggleLanguage: () => undefined, t: text => text, format: salesFormat, labels: salesLabels };
const SalesLocaleContext = createContext<SalesLocale | null>(null);
/** Reuse Sales presentation in another CRM workspace without changing global preferences. */
export function SalesComponentLocaleProvider({ children, language, copy = {} }: {
  children: ReactNode; language: SalesLanguage; copy?: Record<string, string>;
}) {
  const t = <T,>(value: T): T => (typeof value === 'string'
    ? copy[value] ?? translateSalesText(value, language) : value) as T;
  const value: SalesLocale = { ...englishFallback, language, t,
    format: (template, values) => interpolateTemplate(t(template), values),
    labels: config => translateLabels(config, language) };
  return <SalesLocaleContext.Provider value={value}>{children}</SalesLocaleContext.Provider>;
}
export function useSalesLocale(): SalesLocale {
  const context = useContext(SalesLocaleContext);
  const appLanguage = useOptionalLanguage();
  const active = useActiveSalesLanguage();
  const language = context?.language ?? appLanguage?.language ?? active ?? 'en';
  const scoped = context !== null || appLanguage !== undefined || active !== null;
  const t = useCallback(<T,>(value: T): T => (scoped && typeof value === 'string' ? translateSalesText(value, language) : value) as T, [language, scoped]);
  const format = useCallback((template: string, values: readonly unknown[]) => scoped ? formatSalesText(template, values, language) : interpolateTemplate(template, values), [language, scoped]);
  const labels = useCallback(<T,>(config: T): T => scoped ? translateLabels(config, language) : config, [language, scoped]);
  return useMemo(() => context ?? { ...englishFallback, ...(appLanguage ? { setLanguage: appLanguage.setLanguage, toggleLanguage: appLanguage.toggleLanguage } : {}), language, t, format, labels }, [context, appLanguage, language, t, format, labels]);
}

function readLanguage(): SalesLanguage {
  const saved=getAccountLanguage(); if(saved)return saved;
  try { return localStorage.getItem(STORAGE_KEY) === 'ar' ? 'ar' : 'en'; }
  catch { return 'en'; }
}

const languageClasses = ['rtl', 'ltr', 'lang-ar', 'lang-en'];
const attributes = ['lang', 'dir', 'data-sales-language'];
function captureDocument() {
  const elements = [document.documentElement, document.body];
  const previous = elements.map(element => attributes.map(name => element.getAttribute(name)));
  const previousClasses = languageClasses.map(name => document.body.classList.contains(name));
  return () => {
    elements.forEach((element, index) => attributes.forEach((name, attributeIndex) => {
      const value = previous[index][attributeIndex];
      if (value === null) element.removeAttribute(name); else element.setAttribute(name, value);
    }));
    languageClasses.forEach((name, index) => document.body.classList.toggle(name, previousClasses[index]));
  };
}

function applyDocumentLanguage(language: SalesLanguage) {
  const direction = language === 'ar' ? 'rtl' : 'ltr';
  for (const element of [document.documentElement, document.body]) {
    element.lang = language;
    element.dir = direction;
    element.setAttribute('data-sales-language', language);
  }
  languageClasses.forEach(name => document.body.classList.toggle(name, name === direction || name === `lang-${language}`));
}

function StandaloneSalesLocaleRoot({ children }: { children: ReactNode }) {
  const [language, setLocalLanguage] = useState<SalesLanguage>(readLanguage);
  const setLanguage = useStaffLanguageSync(setLocalLanguage);
  return <SalesLocaleRoot language={language} setLanguage={setLanguage}>{children}</SalesLocaleRoot>;
}
function SalesLocaleRoot({ children, language, setLanguage }: {
  children: ReactNode; language: SalesLanguage; setLanguage: (language: SalesLanguage) => void;
}) {
  const appLanguage = useOptionalLanguage();
  const appScoped = appLanguage !== undefined;
  const languageRef = useRef(language);
  const restoreDocumentRef = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    languageRef.current = language;
    publishLanguage(language);
    if (appScoped) for (const element of [document.documentElement, document.body]) element.setAttribute('data-sales-language', language);
    if (!appScoped && restoreDocumentRef.current) applyDocumentLanguage(language);
    try { localStorage.setItem(STORAGE_KEY, language); } catch { /* Private browsing may disallow storage. */ }
  }, [language, appScoped]);
  useEffect(() => {
    if (appScoped) {
      // The app root exclusively owns lang/dir. Keep scoped styling metadata only.
      for (const element of [document.documentElement, document.body]) element.setAttribute('data-sales-language', languageRef.current);
      return () => {
        for (const element of [document.documentElement, document.body]) element.removeAttribute('data-sales-language');
        publishLanguage(null);
        publishDepartmentLocale(null);
      };
    }
    let mounted = true;
    // Public LanguageProvider runs its initial passive effect after its children.
    // Capture its finished metadata before applying the scoped sales choice.
    queueMicrotask(() => {
      if (!mounted) return;
      restoreDocumentRef.current = captureDocument();
      applyDocumentLanguage(languageRef.current);
    });
    return () => {
      mounted = false;
      restoreDocumentRef.current?.();
      restoreDocumentRef.current = null;
      publishLanguage(null);
      publishDepartmentLocale(null);
    };
  }, [appScoped]);
  const toggleLanguage = useCallback(() => setLanguage(language === 'en' ? 'ar' : 'en'), [language,setLanguage]);
  const t = useCallback(<T,>(value: T): T => (typeof value === 'string' ? translateSalesText(value, language) : value) as T, [language]);
  const format = useCallback((template: string, values: readonly unknown[]) => formatSalesText(template, values, language), [language]);
  const labels = useCallback(<T,>(config: T): T => translateLabels(config, language), [language]);
  const value = useMemo(() => ({ language, setLanguage, toggleLanguage, t, format, labels }), [language, setLanguage, toggleLanguage, t, format, labels]);
  useLayoutEffect(() => { publishDepartmentLocale(value); }, [value]);
  return <SalesLocaleContext.Provider value={value}>{children}</SalesLocaleContext.Provider>;
}

export function SalesLocaleProvider({ children }: { children: ReactNode }) {
  const parent = useContext(SalesLocaleContext);
  const appLanguage = useOptionalLanguage();
  if (parent) return <>{children}</>;
  return appLanguage
    ? <SalesLocaleRoot language={appLanguage.language} setLanguage={appLanguage.setLanguage}>{children}</SalesLocaleRoot>
    : <StandaloneSalesLocaleRoot>{children}</StandaloneSalesLocaleRoot>;
}
